import { Component } from '@theme/component';
import { Fleet, selfHeal } from '@theme/fleet';

/**
 * <fleet-fit-badge> — on the product page, shows whether the active saved machine
 * fits THIS product. It asks the backend (/apps/search/fitment-check), which uses the
 * SAME matching as the fitment search, instead of comparing the product's raw
 * custom.fitment metafield — whose un-normalized OEM machine names ("BACKHOE LOADER
 * (LIVERY) - TIER 3 NRC") never matched the cascade's normalized "Backhoe"/"580SN",
 * so the old client-side match always said "doesn't fit". States via [data-state]:
 *   unknown  — no active machine yet (prompts the shopper to pick one)
 *   checking — waiting on the fit check
 *   fits     — backend says this product fits the active machine
 *   nofit    — backend says it does not
 * If the check is unavailable (endpoint down / offline) the badge hides itself rather
 * than show a wrong verdict.
 *
 * Re-renders on `fleet:change` (flips live as the shopper switches machines) and
 * self-heals after Horizon's post-paint section morph. The verdict is fetched once per
 * product+machine combo and cached on the element, so re-renders re-apply it without
 * re-fetching.
 */

const ENDPOINT = '/apps/search/fitment-check';

const ICON_CHECK =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12"/></svg>';
const ICON_X =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" aria-hidden="true"><line x1="6" y1="6" x2="18" y2="18"/><line x1="18" y1="6" x2="6" y2="18"/></svg>';

function esc(s) {
  const d = document.createElement('div');
  d.textContent = s == null ? '' : s;
  return d.innerHTML;
}

class FleetFitBadge extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.body = this.querySelector('[data-ref="body"]');
    this.productGid = this.dataset.productGid || '';

    // Cached verdict for the current product+machine combo:
    //   undefined = not checked, null = checking, true/false = fits, 'err' = unavailable.
    this._key = null;
    this._verdict = undefined;

    this.render = this.render.bind(this);
    // Render now + on fleet:change, and re-render (re-applying the cached verdict)
    // when Horizon's post-paint morph reverts our DOM.
    this._heal = selfHeal(this, this.render);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this._heal) this._heal.stop();
  }

  openFleet() {
    const menu = document.querySelector('fleet-menu');
    if (menu && typeof menu.open === 'function') menu.open();
  }

  render() {
    if (!this.body) return;
    const active = Fleet.getActive();

    // No active machine, or we can't identify this product → prompt to pick one.
    if (!active || !this.productGid) {
      this._key = null;
      this._verdict = undefined;
      this.#apply(null);
      return;
    }

    // Fetch the verdict only when the product+machine combo changes; otherwise just
    // re-paint the cached verdict (so morph/self-heal re-renders don't re-hit the API).
    const key = [this.productGid, active.year, active.machine, active.model].join('|');
    if (key !== this._key) {
      this._key = key;
      this._verdict = null;
      this.#check(active, key);
    }
    this.#apply(active);
  }

  #check(active, key) {
    const params = new URLSearchParams({ productGid: this.productGid });
    if (active.year) params.set('year', active.year);
    if (active.machine) params.set('machine', active.machine);
    if (active.model) params.set('model', active.model);
    fetch(`${ENDPOINT}?${params.toString()}`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (key !== this._key) return; // a newer machine/product superseded this check
        this._verdict = data ? Boolean(data.fits) : 'err';
        this.#apply(Fleet.getActive());
      })
      .catch(() => {
        if (key !== this._key) return;
        this._verdict = 'err';
        this.#apply(Fleet.getActive());
      });
  }

  /** Paint the DOM from the cached verdict. Idempotent + called on every render, so
   *  the badge survives Horizon's morph without re-fetching. */
  #apply(active) {
    if (!this.body) return;

    if (!active) {
      this.style.display = '';
      this.dataset.state = 'unknown';
      this.body.innerHTML =
        '<span class="fleet-fit__text">Select your machine to check fit</span>' +
        '<button type="button" class="fleet-fit__action" on:click="fleet-fit-badge/openFleet">Choose machine</button>';
      return;
    }

    // Check unavailable — hide rather than risk a wrong verdict.
    if (this._verdict === 'err') {
      this.style.display = 'none';
      return;
    }
    this.style.display = '';

    if (this._verdict === null || this._verdict === undefined) {
      this.dataset.state = 'checking';
      this.body.innerHTML = `<span class="fleet-fit__text">Checking fit for ${esc(active.label)}…</span>`;
      return;
    }

    if (this._verdict) {
      this.dataset.state = 'fits';
      this.body.innerHTML =
        `<span class="fleet-fit__icon">${ICON_CHECK}</span>` +
        `<span class="fleet-fit__text">Fits your ${esc(active.label)}</span>`;
    } else {
      this.dataset.state = 'nofit';
      this.body.innerHTML =
        `<span class="fleet-fit__icon">${ICON_X}</span>` +
        `<span class="fleet-fit__text">Doesn't fit your ${esc(active.label)}</span>` +
        '<button type="button" class="fleet-fit__action" on:click="fleet-fit-badge/openFleet">Change machine</button>';
    }
  }
}

if (!customElements.get('fleet-fit-badge')) customElements.define('fleet-fit-badge', FleetFitBadge);
